/**
 * BackorderCustomerOrderTab
 * -------------------------
 * Admin "Build Order" panel — mirrors BackorderCustomerQuoteTab but represents
 * a confirmed fulfillment order / packing form.
 *
 * Flow:
 *   1. Load saved order draft (or empty). If none, offer "Prefill from Build
 *      Quote" one-click populate.
 *   2. Admin edits SKU / description / qty / UOM / unit price / notes per line.
 *   3. Add ship-to, ship-via, tracking, ship date, fulfillment notes, PO number,
 *      status.
 *   4. Save → Preview PDF (opens the fulfillment form in a new tab).
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Save, Eye, Package, ClipboardList, Sparkles,
  Truck, AlertTriangle, CheckCircle2, FileCheck2, Mail, Upload, Paperclip, Download,
  FileScan, Users, Wand2, ExternalLink, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import CrmContactPicker from "@/components/admin/CrmContactPicker";
import ItemSuggestInput from "@/components/admin/ItemSuggestInput";

const API = process.env.REACT_APP_BACKEND_URL;

const UOM_OPTIONS = ["Each", "Case", "Box", "Pack", "Pair", "Roll", "Kit", "Bag"];
const STATUS_OPTIONS = ["draft", "confirmed", "shipped", "delivered"];

const emptyLine = () => ({
  name: "", sku: "", quantity: 1, uom: "Each", unit_price: 0, lead_time: "", notes: "", po_number: "",
});

const REGION_OPTIONS = [
  "Northeast", "Southeast", "Midwest", "South",
  "Southwest", "Northwest", "West Coast", "National",
];

// Several customer POs can ride on one order as long as they ship to the same
// place. Lines stay tagged with the PO they came off so receiving can check
// each PO separately.
const normAddr = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

// "Oct 20 – Oct 22, 2026" — what the hospital reads on the shipped email.
const formatEtaWindow = (from, to, note) => {
  const fmt = (v, withYear) => {
    if (!v) return "";
    const d = new Date(`${v}T00:00:00`);
    if (Number.isNaN(d.getTime())) return v;
    return d.toLocaleDateString("en-US", withYear
      ? { month: "short", day: "numeric", year: "numeric" }
      : { month: "short", day: "numeric" });
  };
  const a = (from || "").trim();
  const b = (to || "").trim();
  let window = "";
  if (a && b && a !== b) window = `${fmt(a, a.slice(0, 4) !== b.slice(0, 4))} – ${fmt(b, true)}`;
  else if (a || b) window = fmt(a || b, true);
  else return "";
  const n = (note || "").trim();
  return n ? `${window} (${n})` : window;
};

const emptyDraft = () => ({
  order_number: "",
  po_number: "",
  pos: [],
  line_items: [emptyLine()],
  freight_percent: 20,
  use_hospital_courier: false,
  hospital_courier_account: "",
  freight_override: "",
  terms: "Net 30",
  ship_to: "",
  ship_to_facility_id: "",
  ship_to_facility_name: "",
  ship_via: "",
  ship_from_region: "",
  tracking_number: "",
  ship_date: "",
  eta_from: "",
  eta_to: "",
  eta_note: "",
  fulfillment_notes: "",
  status: "draft",
  // Purchase-order context — filled by hand, or read off the customer's PO
  // by AskIsaac. Kept on the order so AP and the warehouse see the same terms
  // the customer wrote on their own paperwork.
  po_date: "",
  po_need_by: "",
  bill_to: "",
  buyer_name: "",
  buyer_phone: "",
  buyer_email: "",
  freight_terms: "",
  requisition_number: "",
  contract_number: "",
  tax_exempt_id: "",
  po_total: 0,
});

export default function BackorderCustomerOrderTab({ backorder, token, onRefresh, onOpenOrder }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [totals, setTotals] = useState({ subtotal: 0, freight: 0, total: 0 });
  const [quotePrefill, setQuotePrefill] = useState([]);
  const [quoteNumber, setQuoteNumber] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [attachIds, setAttachIds] = useState([]);
  const [poReading, setPoReading] = useState(false);
  const [poResult, setPoResult] = useState(null);      // { proposal, quote_variance, attachment }
  const [crmPicker, setCrmPicker] = useState(null);    // "to" | "cc" | null
  const [draftingOutlook, setDraftingOutlook] = useState(false);
  const [poDupes, setPoDupes] = useState([]);       // other orders on this PO number
  // Which facility of the health system this order ships to — Ben Taub and LBJ
  // are the same customer and two different docks.
  const [facMatch, setFacMatch] = useState(null);
  const [hospitalId, setHospitalId] = useState("");

  const url = `${API}/api/admin/backorders/${backorder.id}/customer-order`;

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(url, { headers });
      const bo = r.data?.backorder || {};
      const o = r.data?.order;
      setHospitalId(bo.hospital_id || "");
      setQuotePrefill(r.data?.quote_prefill || []);
      setQuoteNumber(r.data?.quote_number || "");
      setAttachments(r.data?.order_form_attachments || []);
      if (o && Array.isArray(o.line_items)) {
        setDraft({
          ...emptyDraft(),
          ...o,
          line_items: o.line_items.length ? o.line_items : [emptyLine()],
          pos: (o.pos || []).length
            ? o.pos
            : (o.po_number ? [{ po_number: o.po_number, po_date: o.po_date || "",
                                po_need_by: o.po_need_by || "", buyer_name: o.buyer_name || "",
                                ship_to: o.ship_to || "", po_total: o.po_total || null }] : []),
          freight_override: o.freight_override == null ? "" : String(o.freight_override),
          ship_to: o.ship_to || bo.shipping_address || "",
          ship_to_facility_id: o.ship_to_facility_id || bo.ship_to_facility_id || "",
          ship_to_facility_name: o.ship_to_facility_name || bo.ship_to_facility_name || "",
          hospital_courier_account: o.hospital_courier_account || bo.hospital_courier_account || "",
          use_hospital_courier: o.use_hospital_courier ?? bo.use_hospital_courier ?? false,
        });
      } else {
        // No saved order — seed ship-to from the backorder's shipping address.
        setDraft(d => ({
          ...d,
          ship_to: bo.shipping_address || "",
          ship_to_facility_id: bo.ship_to_facility_id || "",
          ship_to_facility_name: bo.ship_to_facility_name || "",
          hospital_courier_account: bo.hospital_courier_account || "",
          use_hospital_courier: !!bo.use_hospital_courier,
        }));
      }
      setTotals(r.data?.totals || { subtotal: 0, freight: 0, total: 0 });
    } catch (e) {
      toast.error(`Failed to load order: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [backorder?.id]);

  // Recompute totals locally on every draft change.
  useEffect(() => {
    const sub = (draft.line_items || []).reduce(
      (a, it) => a + (Number(it.unit_price) || 0) * (Number(it.quantity) || 0), 0
    );
    let freight = 0;
    if (draft.use_hospital_courier) freight = 0;
    else if (draft.freight_override !== "" && !isNaN(parseFloat(draft.freight_override)))
      freight = parseFloat(draft.freight_override);
    else freight = sub * ((Number(draft.freight_percent) || 0) / 100);
    setTotals({
      subtotal: Math.round(sub * 100) / 100,
      freight: Math.round(freight * 100) / 100,
      total: Math.round((sub + freight) * 100) / 100,
    });
  }, [draft]);

  const setLine = (idx, patch) => {
    setDraft(d => ({
      ...d,
      line_items: d.line_items.map((it, i) => i === idx ? { ...it, ...patch } : it),
    }));
  };

  // A suggestion fills the line: description, SKU, unit of measure and the
  // price we last used. Quantity is left alone — only the buyer knows that.
  const applySuggestion = (idx, s) => {
    setLine(idx, {
      name: s.name || "",
      sku: s.sku || "",
      ...(s.uom ? { uom: s.uom } : {}),
      ...(s.unit_price != null ? { unit_price: s.unit_price } : {}),
    });
    toast.success(
      s.source === "past_order"
        ? `Filled from a past order${s.last_hospital ? ` · last ordered for ${s.last_hospital}` : ""}`
        : "Filled from the marketplace catalogue",
      { duration: 4000 },
    );
  };

  const addLine = () => setDraft(d => ({ ...d, line_items: [...d.line_items, emptyLine()] }));
  const removeLine = (idx) =>
    setDraft(d => ({ ...d, line_items: d.line_items.filter((_, i) => i !== idx) }));

  // The same PO on two orders is a double entry until somebody says it is a
  // revision — check on every change to the field, not just on upload.
  useEffect(() => {
    const po = (draft.po_number || "").trim();
    if (po.length < 4) { setPoDupes([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(
          `${API}/api/admin/po-intake/check-po-number`
          + `?po_number=${encodeURIComponent(po)}&exclude_id=${encodeURIComponent(backorder.id)}`,
          { headers });
        setPoDupes(r.data?.duplicates || []);
      } catch {
        setPoDupes([]);
      }
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.po_number, backorder.id]);

  // Which docks does this health system have, and which one does the ship-to
  // block describe? Re-checked as the ship-to text is edited.
  useEffect(() => {
    if (!hospitalId) { setFacMatch(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(
          `${API}/api/admin/po-intake/facility-match`
          + `?hospital_id=${encodeURIComponent(hospitalId)}`
          + `&ship_to=${encodeURIComponent(draft.ship_to || "")}`,
          { headers });
        if (!cancelled) setFacMatch(r.data || null);
      } catch {
        if (!cancelled) setFacMatch(null);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitalId, draft.ship_to]);

  const pickFacility = (f) => setDraft(d => ({
    ...d,
    ship_to_facility_id: f?.id || "",
    ship_to_facility_name: f?.name || "",
  }));

  const selectedFacility = (facMatch?.facilities || []).find(
    f => f.id === draft.ship_to_facility_id) || null;

  const etaWindowLabel = useMemo(
    () => formatEtaWindow(draft.eta_from, draft.eta_to, draft.eta_note),
    [draft.eta_from, draft.eta_to, draft.eta_note],
  );

  // A facility label that the printed address contradicts is how product lands
  // at the wrong campus of the right system — say so out loud.
  const facilityAddressMismatch = useMemo(() => {
    if (!selectedFacility || !(draft.ship_to || "").trim()) return false;
    const hay = ` ${(draft.ship_to || "").toLowerCase().replace(/[^a-z0-9]+/g, " ")} `;
    const generic = ["hospital", "medical", "center", "centre", "clinic", "health", "general"];
    const tokens = (selectedFacility.name || "").toLowerCase().split(/[^a-z0-9]+/)
      .filter(w => w.length > 3 && !generic.includes(w));
    const number = ((selectedFacility.address || "").match(/\d+/) || [])[0];
    const zip = (selectedFacility.zip_code || "").slice(0, 5);
    return !(tokens.some(w => hay.includes(` ${w} `))
      || (number && hay.includes(` ${number} `))
      || (zip && hay.includes(` ${zip} `)));
  }, [selectedFacility, draft.ship_to]);

  // ── Customer PO → AskIsaac reads it and proposes the whole order ───────
  const readPurchaseOrder = async (file) => {
    if (!file) return;
    setPoReading(true);
    setPoResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${url}/parse-po`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setPoResult(r.data);
      const lines = r.data?.proposal?.line_items?.length || 0;
      const dupes = r.data?.duplicates || [];
      toast.success(
        `AskIsaac read PO ${r.data?.proposal?.po_number || "(no number)"} — ${lines} line${lines === 1 ? "" : "s"}`,
        { description: "Review the fields below, then apply them to the order." },
      );
      if (dupes.length) {
        toast.error(`That PO is already on ${dupes.map(d => d.backorder_id).join(", ")}`, {
          description: "Check it is not a double entry before you apply it.",
          duration: 12000,
        });
      }
    } catch (e) {
      toast.error(`Could not read that PO: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setPoReading(false);
    }
  };

  // The PO price wins — the customer pays against their own paperwork.
  const applyPoProposal = ({ withLines = true } = {}) => {
    const p = poResult?.proposal;
    if (!p) return;
    const fm = poResult?.facility_match;
    const trusted = fm?.resolved && (fm.source === "alias" || fm.source === "only_facility")
      ? fm.resolved : null;
    setDraft(d => ({
      ...d,
      ship_to_facility_id: trusted?.id || d.ship_to_facility_id,
      ship_to_facility_name: trusted?.name || d.ship_to_facility_name,
      po_number: p.po_number || d.po_number,
      po_date: p.po_date || d.po_date,
      po_need_by: p.po_need_by || d.po_need_by,
      ship_to: p.ship_to || d.ship_to,
      bill_to: p.bill_to || d.bill_to,
      buyer_name: p.buyer_name || d.buyer_name,
      buyer_phone: p.buyer_phone || d.buyer_phone,
      buyer_email: p.buyer_email || d.buyer_email,
      terms: p.terms || d.terms,
      freight_terms: p.freight_terms || d.freight_terms,
      ship_via: p.ship_via || d.ship_via,
      hospital_courier_account: p.shipping_account || d.hospital_courier_account,
      use_hospital_courier: p.shipping_account ? true : d.use_hospital_courier,
      requisition_number: p.requisition_number || d.requisition_number,
      contract_number: p.contract_number || d.contract_number,
      tax_exempt_id: p.tax_exempt_id || d.tax_exempt_id,
      po_total: p.po_total || d.po_total,
      fulfillment_notes: p.fulfillment_notes
        ? [d.fulfillment_notes, p.fulfillment_notes].filter(Boolean).join("\n")
        : d.fulfillment_notes,
      line_items: withLines && (p.line_items || []).length
        ? p.line_items.map(li => ({
            name: li.name, sku: li.sku, quantity: li.quantity,
            uom: li.uom, unit_price: li.unit_price, notes: li.notes,
          }))
        : d.line_items,
    }));
    toast.success(
      withLines ? "PO applied — review and save the order" : "PO header applied — line items untouched",
    );
    setPoResult(r => (r ? { ...r, applied: true } : r));
  };

  // ── Add this PO alongside the one(s) already on the order ───────────────
  // Only allowed when it ships to the same location: receiving gets one
  // shipment, so a different dock has to be its own order.
  const hasExistingPo = (draft.pos || []).length > 0 || !!(draft.po_number || "").trim();

  const shipToConflict = () => {
    const p = poResult?.proposal;
    if (!p) return null;
    const fm = poResult?.facility_match;
    const newFacility = fm?.resolved?.id || "";
    if (newFacility && draft.ship_to_facility_id && newFacility !== draft.ship_to_facility_id) {
      return { theirs: fm.resolved.name, ours: draft.ship_to_facility_name || "this order's dock" };
    }
    const a = normAddr(p.ship_to);
    const b = normAddr(draft.ship_to);
    if (a && b && a !== b) {
      return { theirs: (p.ship_to || "").split("\n")[0], ours: (draft.ship_to || "").split("\n")[0] };
    }
    return null;
  };

  const addPoToOrder = () => {
    const p = poResult?.proposal;
    if (!p) return;
    const po = (p.po_number || "").trim();
    if (!po) {
      toast.error("That PO has no number on it — type one in before adding it to the order.");
      return;
    }
    if ((draft.pos || []).some(x => (x.po_number || "").trim() === po)) {
      toast.error(`PO ${po} is already on this order.`);
      return;
    }
    const conflict = shipToConflict();
    if (conflict) {
      toast.error(`PO ${po} ships to ${conflict.theirs} — this order ships to ${conflict.ours}.`, {
        description: "Multiple POs can only ride on one order when they ship to the same location. Create a separate order for this PO.",
        duration: 14000,
      });
      return;
    }
    const newLines = (p.line_items || []).map(li => ({
      ...emptyLine(),
      name: li.name, sku: li.sku, quantity: li.quantity,
      uom: li.uom, unit_price: li.unit_price, notes: li.notes,
      po_number: po,
    }));
    if (!newLines.length) {
      toast.error("No line items were read off that PO — nothing to add.");
      return;
    }
    setDraft(d => {
      // Tag any untagged existing lines with the order's first PO so the
      // grouped view has no orphans.
      const firstPo = (d.pos?.[0]?.po_number || d.po_number || "").trim();
      const existing = (d.line_items || [])
        .filter(li => (li.name || "").trim() || (li.sku || "").trim())
        .map(li => ({ ...li, po_number: li.po_number || firstPo }));
      const pos = [...(d.pos || [])];
      if (!pos.length && firstPo) {
        pos.push({ po_number: firstPo, po_date: d.po_date || "", po_need_by: d.po_need_by || "",
                   buyer_name: d.buyer_name || "", ship_to: d.ship_to || "", po_total: d.po_total || null });
      }
      pos.push({ po_number: po, po_date: p.po_date || "", po_need_by: p.po_need_by || "",
                 buyer_name: p.buyer_name || "", ship_to: p.ship_to || d.ship_to || "",
                 po_total: p.po_total || null });
      return {
        ...d,
        pos,
        po_number: d.po_number || po,
        line_items: [...existing, ...newLines],
        po_need_by: d.po_need_by || p.po_need_by || "",
        fulfillment_notes: p.fulfillment_notes
          ? [d.fulfillment_notes, `PO ${po}: ${p.fulfillment_notes}`].filter(Boolean).join("\n")
          : d.fulfillment_notes,
      };
    });
    toast.success(`PO ${po} added — ${newLines.length} line${newLines.length === 1 ? "" : "s"} appended`, {
      description: "Same ship-to, so it rides on this order. Save to keep it.",
    });
    setPoResult(r => (r ? { ...r, applied: true } : r));
  };

  const removePoFromOrder = (po) => {
    setDraft(d => {
      const pos = (d.pos || []).filter(x => (x.po_number || "").trim() !== po);
      const line_items = (d.line_items || []).filter(li => (li.po_number || "").trim() !== po);
      return {
        ...d,
        pos,
        po_number: pos[0]?.po_number || "",
        line_items: line_items.length ? line_items : [emptyLine()],
      };
    });
    toast.success(`PO ${po} and its lines removed — save the order to keep it`);
  };

  // Per-PO contents, for the grouped breakdown panel.
  const poGroups = useMemo(() => {
    const pos = (draft.pos || []).filter(p => (p.po_number || "").trim());
    if (pos.length < 1) return [];
    return pos.map(p => {
      const key = (p.po_number || "").trim();
      const lines = (draft.line_items || []).filter(
        li => ((li.po_number || "").trim() || pos[0].po_number) === key
              && ((li.name || "").trim() || (li.sku || "").trim()));
      return {
        ...p,
        lines,
        subtotal: lines.reduce((s, li) => s + (Number(li.quantity) || 0) * (Number(li.unit_price) || 0), 0),
      };
    });
  }, [draft.pos, draft.line_items]);

  const uploadForm = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${url}/attachments`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setAttachments(a => [...a, r.data.attachment]);
      toast.success(`${r.data.attachment.name} attached to this order`);
    } catch (e) {
      toast.error(`Upload failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setUploading(false);
    }
  };

  const removeForm = async (att) => {
    if (!window.confirm(`Remove ${att.name} from this order?`)) return;
    try {
      await axios.delete(`${url}/attachments/${att.id}`, { headers });
      setAttachments(a => a.filter(x => x.id !== att.id));
      setAttachIds(ids => ids.filter(i => i !== att.id));
      toast.success("Attachment removed");
    } catch (e) {
      toast.error(`Could not remove: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const fileHref = (att) => `${API}${att.url}`;

  const prefillFromQuote = () => {
    if (!quotePrefill.length) {
      toast.info("Save a Build Quote first, then come back here.");
      return;
    }
    setDraft(d => ({
      ...d,
      line_items: quotePrefill.map(q => ({
        name: q.name || "",
        sku: q.sku || "",
        quantity: Number(q.quantity) || 1,
        uom: q.uom || "Each",
        unit_price: Number(q.unit_price) || 0,
        lead_time: q.lead_time || "",
        notes: q.notes || "",
      })),
    }));
    toast.success(`Prefilled ${quotePrefill.length} lines from ${quoteNumber || "quote"}`);
  };

  const save = async ({ autoCompose = false } = {}) => {
    setSaving(true);
    try {
      const payload = {
        ...draft,
        freight_override: draft.freight_override === "" ? null : parseFloat(draft.freight_override),
        line_items: draft.line_items.map(it => ({
          name: it.name || "",
          sku: it.sku || null,
          quantity: parseInt(it.quantity, 10) || 0,
          uom: it.uom || "Each",
          unit_price: parseFloat(it.unit_price) || 0,
          lead_time: (it.lead_time || "").trim() || null,
          notes: it.notes || null,
          po_number: (it.po_number || "").trim() || null,
        })),
        pos: (draft.pos || []).filter(p => (p.po_number || "").trim()),
      };
      const r = await axios.post(url, payload, { headers });
      setDraft(d => ({ ...d, order_number: r.data?.order?.order_number || d.order_number }));
      setTotals(r.data?.totals || totals);
      toast.success(`Order ${r.data?.order?.order_number || ""} saved.`);
      if (onRefresh) onRefresh();
      // Building the order is the trigger for the customer letter — open it
      // already written, instead of making them find the button.
      if (autoCompose && payload.line_items.some(li => li.name)) {
        openConfirmDialog();
      }
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const preview = async () => {
    if (!draft.line_items?.length || !draft.line_items[0]?.name) {
      toast.error("Add at least one line item before previewing.");
      return;
    }
    // Save first so the PDF reflects the latest draft.
    await save();
    const pdfUrl = `${url}/pdf`;
    const r = await axios.get(pdfUrl, { headers, responseType: "blob" });
    const blobUrl = URL.createObjectURL(r.data);
    window.open(blobUrl, "_blank", "noopener");
  };

  // -----------------------------------------------------------------
  // Hospital notification actions — reuse the existing Fulfillment
  // milestone pipeline so the hospital gets the same branded emails
  // that the Fulfillment tab already sends.
  // -----------------------------------------------------------------
  const [notifying, setNotifying] = useState(null); // "shipped" | "delayed" | "arrived" | null

  // Best-effort parse of "UPS Ground" / "FedEx Priority" / "USPS Priority Mail"
  // into a courier code the /shipments endpoint accepts.
  const inferCourier = (shipVia) => {
    const v = (shipVia || "").toUpperCase();
    if (v.includes("UPS")) return "UPS";
    if (v.includes("FEDEX") || v.includes("FED EX")) return "FedEx";
    if (v.includes("USPS")) return "USPS";
    if (v.includes("DHL")) return "DHL";
    return "UPS";
  };

  const sendShipped = async () => {
    const tn = (draft.tracking_number || "").trim();
    if (!tn) {
      toast.error("Add a tracking # on the order first — hospital needs it to track.");
      return;
    }
    if (!draft.line_items?.length || !draft.line_items[0]?.name) {
      toast.error("Add at least one line item first.");
      return;
    }
    setNotifying("shipped");
    try {
      await save();
      const courier = inferCourier(draft.ship_via);
      const etaWindow = formatEtaWindow(draft.eta_from, draft.eta_to, draft.eta_note);
      // Build a single shipment representing everything in this order so the
      // backend keeps its per-shipment sync + status auto-flip working.
      const shipment = {
        courier,
        tracking_number: tn,
        shipment_type: "full",
        ship_date: (draft.ship_date || "").trim() || null,
        line_items: draft.line_items.map((it, idx) => ({
          item_index: idx,
          name: it.name || "",
          quantity_shipped: parseInt(it.quantity, 10) || 0,
        })),
      };
      await axios.put(
        `${API}/api/admin/backorders/${backorder.id}/shipments`,
        { shipments: [shipment] },
        { headers },
      );
      await axios.post(
        `${API}/api/admin/backorders/${backorder.id}/milestones`,
        { type: "shipped", courier, tracking_number: tn,
          ship_date: (draft.ship_date || "").trim() || null,
          estimated_arrival: etaWindow || null,
          note: draft.fulfillment_notes || "" },
        { headers },
      );
      setDraft(d => ({ ...d, status: "shipped" }));
      toast.success(`Tracking sent to ${backorder.hospital_name || "hospital"} — ${courier} ${tn}`);
      onRefresh?.();
    } catch (e) {
      toast.error(`Failed to send tracking: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setNotifying(null);
    }
  };

  const sendDelay = async () => {
    const eta = window.prompt(
      "New estimated arrival date (e.g. 2026-09-30 or 'Sep 30'):",
      formatEtaWindow(draft.eta_from, draft.eta_to, "") || draft.ship_date || "",
    );
    if (!eta) return;
    const reason = window.prompt("Optional — one-line reason to share with the hospital:", "") || "";
    setNotifying("delayed");
    try {
      await save();
      await axios.post(
        `${API}/api/admin/backorders/${backorder.id}/milestones`,
        { type: "delayed", estimated_arrival: eta, note: reason },
        { headers },
      );
      toast.success(`Delay notice sent — new ETA ${eta}`);
      onRefresh?.();
    } catch (e) {
      toast.error(`Failed to send delay: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setNotifying(null);
    }
  };

  // Order-confirmation compose dialog state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, to: "", cc: "", body: "" });
  const [savingRecipients, setSavingRecipients] = useState(false);
  const [attachQuotePdf, setAttachQuotePdf] = useState(true);
  const [letterTemplates, setLetterTemplates] = useState([]);
  const [activeTemplateId, setActiveTemplateId] = useState("");
  const [recipientsSavedAt, setRecipientsSavedAt] = useState(null);

  const parseEmails = (s) =>
    (s || "").split(/[,;\s]+/).map(x => x.trim()).filter(Boolean);

  // Recipients used to live only in this dialog's state, so every reopen lost
  // what you typed. They now persist on the order.
  const saveRecipients = async ({ to, cc, quiet = false } = {}) => {
    const toList = parseEmails(to ?? confirmDialog.to);
    const ccList = parseEmails(cc ?? confirmDialog.cc);
    if (!toList.length && !ccList.length) return false;
    const recipients = [
      ...toList.map((email, i) => ({ email, role: i === 0 ? "primary" : "cc" })),
      ...ccList.filter(e => !toList.includes(e)).map(email => ({ email, role: "cc" })),
    ];
    setSavingRecipients(true);
    try {
      await axios.put(
        `${API}/api/admin/backorders/${backorder.id}/recipients`,
        { recipients },
        { headers },
      );
      setRecipientsSavedAt(new Date().toISOString());
      if (!quiet) {
        toast.success("Recipients saved to this order", {
          description: `${toList.join(", ")}${ccList.length ? ` · cc ${ccList.join(", ")}` : ""}`,
        });
      }
      onRefresh?.();
      return true;
    } catch (e) {
      toast.error(`Could not save recipients: ${e?.response?.data?.detail || e.message}`);
      return false;
    } finally {
      setSavingRecipients(false);
    }
  };

  const openConfirmDialog = async () => {
    setAttachIds(attachments.map(a => a.id));
    const list = await loadLetterTemplates();
    const tpl = list.find(t => t.id === activeTemplateId) || list.find(t => t.is_default);
    if (tpl) setActiveTemplateId(tpl.id);
    const startingBody = renderLetter(tpl?.body);
    setConfirmDialog({ open: true, to: "", cc: "", body: startingBody });
    // Saved recipients win — they are what a human last chose for this order.
    let to = "";
    let cc = "";
    try {
      const r = await axios.get(
        `${API}/api/admin/backorders/${backorder.id}/recipients`, { headers });
      const saved = r.data?.recipients || [];
      to = saved.filter(x => x.role === "primary" && x.email).map(x => x.email).join(", ");
      cc = saved.filter(x => x.role === "cc" && x.email).map(x => x.email).join(", ");
      if (saved.length) setRecipientsSavedAt(r.data?.recipients_updated_at || "saved");
    } catch {
      /* fall through to the derived addresses below */
    }
    if (!to) {
      const primary = (backorder.recipients || []).find(r => r.role === "primary" && r.email);
      to = backorder.contact_email || backorder.hospital_email || primary?.email || "";
      cc = (backorder.recipients || [])
        .filter(r => r.email && r.email !== to)
        .map(r => r.email)
        .join(", ");
    }
    setConfirmDialog({ open: true, to, cc, body: startingBody });
  };
  const closeConfirmDialog = () => setConfirmDialog({ open: false, to: "", cc: "", body: "" });

  // The letter is a template plus the order's own values. Saving a template
  // reverses the substitution so the next order fills itself in.
  const letterValues = () => {
    const rawName = (backorder.contact_name || "").trim();
    const name = (/[a-z]/i.test(rawName) ? rawName : (backorder.hospital_name || "").trim()) || "there";
    const po = (draft.po_number || "").trim();
    const lines = (draft.line_items || []).filter(it => (it.name || "").trim());
    const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined,
      { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const items = lines.map((it, i) => {
      const qty = Number(it.quantity) || 0;
      const price = Number(it.unit_price) || 0;
      return [
        `${i + 1}. ${(it.name || "").trim()}`,
        it.sku ? `   Item #: ${it.sku}` : "",
        `   Qty: ${qty} ${it.uom || "Each"}   Unit: ${money(price)}   Extended: ${money(qty * price)}`,
        `   Lead time: ${(it.lead_time || "").trim() || "confirmed at order entry"}`,
        (it.notes || "").trim() ? `   Note: ${it.notes.trim()}` : "",
      ].filter(Boolean).join("\n");
    }).join("\n\n");
    const subtotal = lines.reduce(
      (s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
    const logistics = [
      draft.ship_from_region ? `Ships from: ${draft.ship_from_region} region` : "",
      draft.ship_via ? `Ship via: ${draft.ship_via}` : "",
      etaWindowLabel ? `Estimated arrival: ${etaWindowLabel}` : "",
      `Terms: ${draft.terms || "Net 30"}`,
    ].filter(Boolean).join("\n");

    return {
      po_line: po ? `PO #${po}\n\n` : "",
      po_number: po,
      contact_name: name,
      hospital: backorder.hospital_name || "",
      order_number: draft.order_number || "",
      items: items || "(no line items yet)",
      total: money(totals?.total || subtotal),
      region: draft.ship_from_region || "",
      ship_via: draft.ship_via || "",
      eta: etaWindowLabel || "",
      terms: draft.terms || "Net 30",
      logistics,
    };
  };

  const DEFAULT_LETTER = `{{po_line}}Hi {{contact_name}},

Thank you for your order — your quote is attached for your records.

ORDER SUMMARY
{{items}}

Order total: {{total}}

{{logistics}}

I will send tracking as soon as it becomes available.

Thank you for your business!

Kind Regards,
George`;

  const renderLetter = (template) => {
    const v = letterValues();
    return (template || DEFAULT_LETTER).replace(
      /\{\{(\w+)\}\}/g, (m, key) => (key in v ? v[key] : m));
  };

  // Turn the admin's edited letter back into a template by swapping this
  // order's values for their tokens — longest values first so "Net 30" inside
  // the logistics block doesn't get replaced before the block itself.
  const tokenizeLetter = (text) => {
    const v = letterValues();
    const keys = ["logistics", "items", "po_line", "total", "eta", "region",
                  "ship_via", "terms", "contact_name", "hospital", "po_number", "order_number"];
    let out = text || "";
    keys
      .filter(k => (v[k] || "").trim().length > 2)
      .sort((a, b) => (v[b] || "").length - (v[a] || "").length)
      .forEach(k => { out = out.split(v[k]).join(`{{${k}}}`); });
    return out;
  };

  const confirmBodyPlain = () => {
    const tpl = letterTemplates.find(t => t.id === activeTemplateId)
      || letterTemplates.find(t => t.is_default);
    return renderLetter(tpl?.body);
  };
  const loadLetterTemplates = async () => {
    try {
      const r = await axios.get(
        `${API}/api/admin/letter-templates?scope=order_confirmation`, { headers });
      const list = r.data?.templates || [];
      setLetterTemplates(list);
      return list;
    } catch {
      return [];
    }
  };

  const saveLetterTemplate = async () => {
    const name = window.prompt(
      "Save this wording as a template — name it (e.g. 'Standard order confirmation'):",
      "Standard order confirmation");
    if (!name?.trim()) return;
    const makeDefault = window.confirm(
      "Use this template as the default for every new order?\n\nOK = yes, make it the default.");
    try {
      const r = await axios.post(`${API}/api/admin/letter-templates`, {
        name: name.trim(),
        scope: "order_confirmation",
        body: tokenizeLetter(confirmDialog.body),
        is_default: makeDefault,
      }, { headers });
      const list = await loadLetterTemplates();
      const saved = r.data?.template;
      if (saved) setActiveTemplateId(saved.id);
      toast.success(`Template "${saved?.name || name}" saved${makeDefault ? " as the default" : ""}`
        + ` — ${list.length} saved`);
    } catch (e) {
      toast.error(`Could not save template: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const applyLetterTemplate = (id) => {
    setActiveTemplateId(id);
    const tpl = letterTemplates.find(t => t.id === id);
    setConfirmDialog(d => ({ ...d, body: renderLetter(tpl?.body) }));
  };

  const deleteLetterTemplate = async () => {
    const tpl = letterTemplates.find(t => t.id === activeTemplateId);
    if (!tpl) return;
    if (!window.confirm(`Delete the template "${tpl.name}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin/letter-templates/${tpl.id}`, { headers });
      setActiveTemplateId("");
      await loadLetterTemplates();
      toast.success("Template deleted");
    } catch (e) {
      toast.error(`Could not delete: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const openInOutlook = () => {
    const to = (confirmDialog.to || "").trim();
    if (!to) { toast.error("Add at least one To recipient"); return; }
    const cc = (confirmDialog.cc || "").trim();
    const po = (draft.po_number || "").trim();
    const subject = po
      ? `Order confirmation — PO #${po}`
      : `Order confirmation — ${backorder.id || ""}`;
    const params = new URLSearchParams({ subject, body: confirmDialog.body || confirmBodyPlain() });
    if (cc) params.set("cc", cc);
    // mailto: cannot carry attachments — download the selected forms so they
    // are sitting in Downloads, ready to drag into the Outlook draft.
    const selected = attachments.filter(a => attachIds.includes(a.id));
    selected.forEach((att, i) => {
      setTimeout(() => {
        const a = document.createElement("a");
        a.href = fileHref(att);
        a.download = att.name;
        a.click();
      }, i * 400);
    });
    if (selected.length) {
      toast.info(
        `${selected.length} form${selected.length === 1 ? "" : "s"} downloading — drag into the Outlook draft`,
        { duration: 6000 },
      );
    }
    // Encode the mailto URL — some Outlook clients need `mailto:to?subject=…&body=…`
    window.location.href = `mailto:${encodeURIComponent(to)}?${params.toString()}`;
  };

  const addRecipients = (contacts, target) => {
    const emails = (contacts || []).map(c => (c.email || "").trim()).filter(Boolean);
    if (!emails.length) return;
    let next = confirmDialog;
    setConfirmDialog(d => {
      const existing = (d[target] || "").split(/[,;\s]+/).map(x => x.trim()).filter(Boolean);
      const merged = Array.from(new Set([...existing, ...emails]));
      next = { ...d, [target]: merged.join(", ") };
      return next;
    });
    toast.success(
      `${emails.length} CRM contact${emails.length === 1 ? "" : "s"} added to ${target.toUpperCase()}`,
    );
    // Persist straight away — picking someone from the CRM and losing them on
    // the next reopen is the bug this whole flow had.
    setTimeout(() => saveRecipients({ to: next.to, cc: next.cc, quiet: true }), 0);
  };

  // Writes the acknowledgment into Outlook Drafts with the sales order already
  // attached, so the only thing left is pressing Send. mailto: can't carry a
  // file, which is why the old path had to download it for dragging.
  const draftInOutlook = async () => {
    const to_emails = (confirmDialog.to || "").split(/[,;\s]+/).map(s2 => s2.trim()).filter(Boolean);
    if (!to_emails.length) { toast.error("Add at least one To recipient"); return; }
    const cc_emails = (confirmDialog.cc || "").split(/[,;\s]+/).map(s2 => s2.trim()).filter(Boolean);
    const po = (draft.po_number || "").trim();
    setDraftingOutlook(true);
    try {
      await save();
      await saveRecipients({ quiet: true });
      const r = await axios.post(`${url}/outlook-draft`, {
        to_emails,
        cc_emails,
        subject: po ? `Order confirmation — PO #${po}` : `Order confirmation — ${backorder.id || ""}`,
        body_text: confirmDialog.body || confirmBodyPlain(),
        attachment_ids: attachIds,
        attach_quote_pdf: attachQuotePdf,
        mark_confirmed: true,
      }, { headers });
      setDraft(d => ({ ...d, status: "confirmed" }));
      if (r.data?.web_link) window.open(r.data.web_link, "_blank", "noopener");
      toast.success(
        `Draft waiting in Outlook (${r.data?.mailbox || "orders@sterilemate.com"})`,
        {
          description: r.data?.attachment_count
            ? `${r.data.attachment_count} form(s) already attached — just press Send.`
            : "No form attached — press Send when you're ready.",
          duration: 8000,
        },
      );
      closeConfirmDialog();
      onRefresh?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail?.message : detail;
      if (e?.response?.status === 503) {
        toast.error("Outlook drafting is not available yet", {
          description: `${msg || ""} Falling back to the mailto draft.`,
          duration: 10000,
        });
        openInOutlook();
      } else {
        toast.error(`Could not create the Outlook draft: ${msg || e.message}`);
      }
    } finally {
      setDraftingOutlook(false);
    }
  };

  const sendConfirmed = async () => {
    const to_emails = (confirmDialog.to || "")
      .split(/[,\s;]+/)
      .map(s => s.trim())
      .filter(Boolean);
    const cc_emails = (confirmDialog.cc || "")
      .split(/[,\s;]+/)
      .map(s => s.trim())
      .filter(Boolean);
    if (to_emails.length === 0) {
      toast.error("Add at least one To recipient");
      return;
    }
    setNotifying("confirmed");
    try {
      await save();
      await saveRecipients({ quiet: true });
      await axios.post(
        `${API}/api/admin/backorders/${backorder.id}/milestones`,
        {
          type: "confirmed",
          to_emails,
          cc_emails,
          po_number: (draft.po_number || "").trim(),
          ship_date: (draft.ship_date || "").trim() || null,
          custom_body: confirmDialog.body || confirmBodyPlain(),
          note: draft.fulfillment_notes || "",
          attachment_ids: attachIds,
          attach_quote_pdf: attachQuotePdf,
        },
        { headers },
      );
      setDraft(d => ({ ...d, status: "confirmed" }));
      toast.success(`Order confirmation sent to ${to_emails.join(", ")}`, {
        description: attachIds.length
          ? `${attachIds.length} form${attachIds.length === 1 ? "" : "s"} attached`
          : "No form attached",
      });
      closeConfirmDialog();
      onRefresh?.();
    } catch (e) {
      toast.error(`Failed to send order confirmation: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setNotifying(null);
    }
  };

  const sendArrivingToday = async () => {
    const saved = (backorder.recipients || []).filter(r => r.email);
    const to = saved.filter(r => r.role === "primary").map(r => r.email);
    const cc = saved.filter(r => r.role === "cc").map(r => r.email);
    const fallback = backorder.contact_email || backorder.hospital_email || "";
    const toList = to.length ? to : (fallback ? [fallback] : []);
    if (!toList.length) {
      toast.error("No saved recipient for this order", {
        description: "Open “Send Order Confirmation” and save a To address first.",
      });
      return;
    }
    if (!window.confirm(
      `Tell ${backorder.hospital_name || "the hospital"} their order arrives today?\n\n`
      + `To: ${toList.join(", ")}${cc.length ? `\nCc: ${cc.join(", ")}` : ""}`
    )) return;
    setNotifying("arriving_today");
    try {
      await save();
      await axios.post(
        `${API}/api/admin/backorders/${backorder.id}/milestones`,
        {
          type: "arriving_today",
          to_emails: toList,
          cc_emails: cc,
          po_number: (draft.po_number || "").trim(),
          courier: draft.courier || backorder.courier || "",
          tracking_number: draft.tracking_number || backorder.tracking_number || "",
          ship_date: (draft.ship_date || "").trim() || null,
          note: draft.fulfillment_notes || "",
        },
        { headers },
      );
      toast.success("Arriving-today notice sent", {
        description: `Receiving at ${backorder.hospital_name || "the hospital"} has been told to expect it today.`,
      });
      onRefresh?.();
    } catch (e) {
      toast.error(`Failed to send arriving-today notice: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setNotifying(null);
    }
  };

  const sendDelivered = async () => {
    if (!window.confirm("Send delivery confirmation email to the hospital?")) return;
    setNotifying("arrived");
    try {
      await save();
      await axios.post(
        `${API}/api/admin/backorders/${backorder.id}/milestones`,
        { type: "arrived", note: draft.fulfillment_notes || "" },
        { headers },
      );
      setDraft(d => ({ ...d, status: "delivered" }));
      toast.success(`Delivery confirmation sent to ${backorder.hospital_name || "hospital"}`);
      onRefresh?.();
    } catch (e) {
      toast.error(`Failed to send delivery notice: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setNotifying(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm py-8" data-testid="build-order-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading order…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="build-order-tab">
      {/* Top row — order # + status + prefill */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              Order #
            </label>
            <Input
              value={draft.order_number || ""}
              onChange={(e) => setDraft(d => ({ ...d, order_number: e.target.value }))}
              placeholder="auto-generated"
              className="w-40 text-sm"
              data-testid="order-number-input"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              Customer PO
            </label>
            <Input
              value={draft.po_number || ""}
              onChange={(e) => setDraft(d => ({ ...d, po_number: e.target.value }))}
              placeholder="PO reference"
              className={`w-40 text-sm ${poDupes.length ? "border-red-400 bg-red-50" : ""}`}
              data-testid="order-po-input"
            />
            {poDupes.length > 0 && (
              <div className="mt-1 text-[10px] text-red-700 font-semibold max-w-[260px] leading-snug"
                data-testid="po-duplicate-warning">
                <AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5" />
                Already on {poDupes.length === 1 ? "order" : "orders"}{" "}
                {poDupes.map((d, i) => (
                  <span key={d.backorder_id}>
                    {i > 0 ? ", " : ""}
                    <button type="button" onClick={() => onOpenOrder?.(d.backorder_id)}
                      className="underline hover:no-underline font-bold"
                      data-testid={`po-dupe-link-${d.backorder_id}`}>
                      {d.backorder_id}
                    </button>
                    {d.hospital_name ? ` (${d.hospital_name})` : ""}
                    {d.match === "partial" ? " — near match" : ""}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              PO date
            </label>
            <Input
              value={draft.po_date || ""}
              onChange={(e) => setDraft(d => ({ ...d, po_date: e.target.value }))}
              placeholder="as printed"
              className="w-28 text-sm"
              data-testid="order-po-date-input"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              Need by
            </label>
            <Input
              value={draft.po_need_by || ""}
              onChange={(e) => setDraft(d => ({ ...d, po_need_by: e.target.value }))}
              placeholder="as printed"
              className="w-28 text-sm"
              data-testid="order-po-needby-input"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              Status
            </label>
            <select
              value={draft.status}
              onChange={(e) => setDraft(d => ({ ...d, status: e.target.value }))}
              className="border border-slate-300 rounded-md h-9 px-2 text-sm"
              data-testid="order-status-select"
            >
              {STATUS_OPTIONS.map(s => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={prefillFromQuote}
          disabled={!quotePrefill.length}
          className="text-xs border-cyan-300 text-cyan-800 hover:bg-cyan-50 disabled:opacity-50"
          data-testid="order-copy-from-quote-btn"
          title={quotePrefill.length ? "Copy line items from Build Quote" : "Save a Build Quote first"}
        >
          <Sparkles className="w-3.5 h-3.5 mr-1.5" />
          Copy from Quote{quoteNumber ? ` (${quoteNumber})` : ""}
        </Button>
      </div>

      {/* Line items */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="bg-slate-900 text-white text-[11px] font-bold uppercase tracking-wider grid grid-cols-[110px_1fr_70px_90px_100px_90px_100px_36px] gap-2 px-3 py-2">
          <div>SKU</div>
          <div>Description</div>
          <div className="text-center">Qty</div>
          <div>UOM</div>
          <div className="text-right">Unit $</div>
          <div>Lead Time</div>
          <div className="text-right">Ext $</div>
          <div></div>
        </div>
        {draft.line_items.map((it, idx) => (
          <div
            key={idx}
            className="grid grid-cols-[110px_1fr_70px_90px_100px_90px_100px_36px] gap-2 px-3 py-2 border-b border-slate-100 items-start"
            data-testid={`order-line-${idx}`}
          >
            <ItemSuggestInput
              value={it.sku || ""}
              onChange={(v) => setLine(idx, { sku: v })}
              onPick={(s) => applySuggestion(idx, s)}
              placeholder="SKU"
              className="h-8 text-xs"
              testId={`order-line-sku-${idx}`}
              token={token}
            />
            <div className="space-y-1">
              <ItemSuggestInput
                value={it.name || ""}
                onChange={(v) => setLine(idx, { name: v })}
                onPick={(s) => applySuggestion(idx, s)}
                placeholder="Product description"
                className="h-8 text-xs"
                testId={`order-line-name-${idx}`}
                token={token}
              />
              <Input
                value={it.notes || ""}
                onChange={(e) => setLine(idx, { notes: e.target.value })}
                placeholder="Notes (optional)"
                className="h-7 text-[11px] text-slate-500"
                data-testid={`order-line-notes-${idx}`}
              />
            </div>
            <Input
              type="number"
              min="0"
              value={it.quantity ?? 1}
              onChange={(e) => setLine(idx, { quantity: e.target.value })}
              className="h-8 text-xs text-center"
              data-testid={`order-line-qty-${idx}`}
            />
            <select
              value={it.uom || "Each"}
              onChange={(e) => setLine(idx, { uom: e.target.value })}
              className="border border-slate-300 rounded-md h-8 px-1 text-xs"
              data-testid={`order-line-uom-${idx}`}
            >
              {UOM_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={it.unit_price ?? 0}
              onChange={(e) => setLine(idx, { unit_price: e.target.value })}
              className="h-8 text-xs text-right"
              data-testid={`order-line-price-${idx}`}
            />
            <Input
              value={it.lead_time || ""}
              onChange={(e) => setLine(idx, { lead_time: e.target.value })}
              placeholder="5-7 days"
              className="h-8 text-xs"
              data-testid={`order-line-lead-${idx}`}
            />
            <div className="h-8 flex items-center justify-end text-xs font-semibold text-slate-700 tabular-nums">
              ${((Number(it.quantity) || 0) * (Number(it.unit_price) || 0)).toFixed(2)}
            </div>
            <button
              type="button"
              onClick={() => removeLine(idx)}
              className="h-8 w-8 inline-flex items-center justify-center text-red-500 hover:bg-red-50 rounded"
              disabled={draft.line_items.length === 1}
              title="Remove line"
              data-testid={`order-line-remove-${idx}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addLine}
          className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-semibold text-cyan-700 hover:bg-cyan-50 border-t border-slate-200"
          data-testid="order-add-line-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Add line item
        </button>
      </div>

      {/* Ship-to / Ship-via block */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">
            Ship To
          </label>
          {facMatch && facMatch.facility_count > 0 ? (
            <div className={`rounded-md border p-2 ${draft.ship_to_facility_id
              ? "border-emerald-300 bg-emerald-50/50" : "border-amber-300 bg-amber-50/60"}`}
              data-testid="order-shipto-facility-block">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600 inline-flex items-center gap-1">
                  <Truck className="w-3 h-3" /> Facility
                  {draft.ship_to_facility_id
                    ? <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                    : <span className="text-amber-700">· not set</span>}
                </span>
                {draft.ship_to_facility_id ? (
                  <button type="button" onClick={() => pickFacility(null)}
                    className="text-[10px] font-semibold text-slate-500 hover:text-slate-800"
                    data-testid="order-shipto-facility-clear">Clear</button>
                ) : null}
              </div>
              <select
                value={draft.ship_to_facility_id || ""}
                onChange={(e) => pickFacility(
                  (facMatch.facilities || []).find(f => f.id === e.target.value) || null)}
                className="mt-1 w-full text-sm rounded border border-slate-300 bg-white px-2 py-1.5"
                data-testid="order-shipto-facility-select">
                <option value="">Ship to the address as printed below</option>
                {(facMatch.facilities || []).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}{f.city ? ` — ${f.city}` : ""}{f.score >= 45 ? " (likely)" : ""}
                  </option>
                ))}
              </select>
              {!draft.ship_to_facility_id && facMatch.resolved && facMatch.facility_count >= 2 ? (
                <button type="button" onClick={() => pickFacility(facMatch.resolved)}
                  className="mt-1 text-[10px] font-bold text-cyan-700 hover:underline"
                  data-testid="order-shipto-facility-suggestion">
                  Ship-to looks like {facMatch.resolved.name} — use it
                </button>
              ) : null}
              {facilityAddressMismatch ? (
                <div className="mt-1.5 rounded bg-amber-100 border border-amber-300 px-2 py-1.5"
                  data-testid="order-shipto-mismatch-warning">
                  <p className="text-[10px] font-bold text-amber-900 inline-flex items-start gap-1">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    The printed address below does not mention {draft.ship_to_facility_name} — one of
                    the two is the wrong campus.
                  </p>
                  <button type="button"
                    onClick={() => setDraft(d => ({ ...d, ship_to: selectedFacility.block || d.ship_to }))}
                    className="mt-1 text-[10px] font-bold text-amber-900 underline hover:no-underline"
                    data-testid="order-shipto-use-facility-address">
                    Replace it with the {draft.ship_to_facility_name} address on file
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
          <Textarea
            value={draft.ship_to || ""}
            onChange={(e) => setDraft(d => ({ ...d, ship_to: e.target.value }))}
            placeholder="Hospital / dock / suite address"
            rows={3}
            className="text-sm"
            data-testid="order-ship-to"
          />
        </div>
        <div className="space-y-2">
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">
            Ship Via
          </label>
          <Input
            value={draft.ship_via || ""}
            onChange={(e) => setDraft(d => ({ ...d, ship_via: e.target.value }))}
            placeholder="UPS Ground / FedEx / LTL / Hospital courier"
            className="text-sm"
            data-testid="order-ship-via"
          />
          {/* Which stocking region this order ships out of — prints on the quote
              and the customer PDF, and is named in the confirmation letter. */}
          <div className="rounded-md border border-slate-200 bg-slate-50 p-2" data-testid="order-region-block">
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
              Ships from region
            </label>
            <div className="flex flex-wrap gap-1.5">
              {REGION_OPTIONS.map((rg) => {
                const on = draft.ship_from_region === rg;
                return (
                  <button
                    key={rg}
                    type="button"
                    onClick={() => setDraft(d => ({ ...d, ship_from_region: on ? "" : rg }))}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition-colors ${on
                      ? "bg-cyan-600 text-white"
                      : "bg-white border border-slate-300 text-slate-600 hover:border-cyan-400 hover:text-cyan-700"}`}
                    data-testid={`order-region-${rg.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {rg}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={draft.tracking_number || ""}
              onChange={(e) => setDraft(d => ({ ...d, tracking_number: e.target.value }))}
              placeholder="Tracking #"
              className="text-sm"
              data-testid="order-tracking"
            />
            <div>
              <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">
                Date shipped
              </label>
              <Input
                type="date"
                value={draft.ship_date || ""}
                onChange={(e) => setDraft(d => ({ ...d, ship_date: e.target.value }))}
                className="text-sm"
                data-testid="order-ship-date"
              />
            </div>
          </div>
          <p className="text-[10px] text-slate-500">
            The day it actually left the dock — set it yourself. Nothing is assumed to be today, and
            the emails only name a date when you give one.
          </p>
          {/* Estimated arrival window — rides along on the shipped email and the order PDF */}
          <div className="rounded-md border border-sky-200 bg-sky-50/60 p-2">
            <label className="block text-[10px] font-bold uppercase tracking-wider text-sky-800 mb-1">
              Estimated arrival window
            </label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">
                  Earliest
                </label>
                <Input
                  type="date"
                  value={draft.eta_from || ""}
                  onChange={(e) => setDraft(d => ({ ...d, eta_from: e.target.value }))}
                  className="text-sm bg-white"
                  data-testid="order-eta-from"
                />
              </div>
              <div>
                <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">
                  Latest
                </label>
                <Input
                  type="date"
                  value={draft.eta_to || ""}
                  onChange={(e) => setDraft(d => ({ ...d, eta_to: e.target.value }))}
                  className="text-sm bg-white"
                  data-testid="order-eta-to"
                />
              </div>
            </div>
            <Input
              value={draft.eta_note || ""}
              onChange={(e) => setDraft(d => ({ ...d, eta_note: e.target.value }))}
              placeholder="Optional note — e.g. AM delivery, dock closes 2pm"
              className="text-sm bg-white mt-2"
              data-testid="order-eta-note"
            />
            {etaWindowLabel ? (
              <p className="text-[10px] text-sky-800 mt-1.5 font-semibold" data-testid="order-eta-window-label">
                Hospital sees: {etaWindowLabel}
              </p>
            ) : (
              <p className="text-[10px] text-slate-500 mt-1.5">
                Leave blank and the shipped email simply won't promise a date.
              </p>
            )}
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={!!draft.use_hospital_courier}
              onChange={(e) => setDraft(d => ({ ...d, use_hospital_courier: e.target.checked }))}
              data-testid="order-use-hospital-courier"
            />
            Hospital pays own freight (courier account)
          </label>
          {draft.use_hospital_courier && (
            <Input
              value={draft.hospital_courier_account || ""}
              onChange={(e) => setDraft(d => ({ ...d, hospital_courier_account: e.target.value }))}
              placeholder="Courier account #"
              className="text-sm"
              data-testid="order-courier-account"
            />
          )}
        </div>
      </div>

      {/* Fulfillment notes */}
      <div>
        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
          Fulfillment Notes
        </label>
        <Textarea
          value={draft.fulfillment_notes || ""}
          onChange={(e) => setDraft(d => ({ ...d, fulfillment_notes: e.target.value }))}
          rows={2}
          placeholder="Packing / delivery instructions — appear on the PDF."
          className="text-sm"
          data-testid="order-fulfillment-notes"
        />
      </div>

      {/* Totals + freight controls */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 grid md:grid-cols-2 gap-4">
        <div className="space-y-2 text-xs">
          <div className="flex items-center gap-2">
            <span className="text-slate-500 font-semibold w-28">Freight %</span>
            <Input
              type="number"
              min="0"
              step="1"
              value={draft.freight_percent}
              onChange={(e) => setDraft(d => ({ ...d, freight_percent: e.target.value }))}
              className="h-7 w-20 text-xs"
              disabled={draft.use_hospital_courier || draft.freight_override !== ""}
              data-testid="order-freight-percent"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-500 font-semibold w-28">Freight $ override</span>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={draft.freight_override}
              onChange={(e) => setDraft(d => ({ ...d, freight_override: e.target.value }))}
              placeholder="—"
              className="h-7 w-24 text-xs"
              disabled={draft.use_hospital_courier}
              data-testid="order-freight-override"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-500 font-semibold w-28">Terms</span>
            <Input
              value={draft.terms}
              onChange={(e) => setDraft(d => ({ ...d, terms: e.target.value }))}
              className="h-7 w-32 text-xs"
              data-testid="order-terms"
            />
          </div>
        </div>
        <div className="text-right text-sm tabular-nums space-y-1">
          <div><span className="text-slate-500 mr-3">Subtotal</span><b>${totals.subtotal.toFixed(2)}</b></div>
          <div><span className="text-slate-500 mr-3">Freight</span><b>${totals.freight.toFixed(2)}</b></div>
          <div className="pt-1 border-t border-slate-300 mt-1 text-base">
            <span className="text-slate-500 mr-3">Total</span>
            <b className="text-slate-900">${totals.total.toFixed(2)}</b>
          </div>
          {Number(draft.po_total) > 0 && (
            <div className={`text-[11px] mt-1 ${
              Math.abs(Number(draft.po_total) - totals.subtotal) < 0.01
                ? "text-emerald-700" : "text-red-700 font-bold"}`}
              data-testid="po-total-reconciliation">
              {Math.abs(Number(draft.po_total) - totals.subtotal) < 0.01
                ? `Matches their PO total of $${Number(draft.po_total).toFixed(2)}`
                : `Their PO says $${Number(draft.po_total).toFixed(2)} for product — ours says $${totals.subtotal.toFixed(2)} (off by $${Math.abs(Number(draft.po_total) - totals.subtotal).toFixed(2)})`}
            </div>
          )}
        </div>
      </div>

      {/* ── POs on this order — contents listed per PO number ───────────── */}
      {poGroups.length > 1 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3" data-testid="order-po-breakdown">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
            <div className="text-[11px] font-black uppercase tracking-wider text-emerald-800 inline-flex items-center gap-1.5">
              <ClipboardList className="w-3.5 h-3.5" />
              {poGroups.length} purchase orders on this order
            </div>
            <span className="text-[10px] text-emerald-900/70">
              Same ship-to — one shipment, one invoice, each PO checked off separately
            </span>
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            {poGroups.map((g) => (
              <div key={g.po_number} className="rounded-md border border-emerald-200 bg-white p-2.5"
                data-testid={`order-po-group-${g.po_number}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-sm font-bold text-slate-900">PO {g.po_number}</div>
                    <div className="text-[10px] text-slate-500">
                      {[g.po_date && `dated ${g.po_date}`, g.po_need_by && `need by ${g.po_need_by}`,
                        g.buyer_name].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold text-emerald-700">
                      ${g.subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {g.lines.length} line{g.lines.length === 1 ? "" : "s"}
                    </div>
                  </div>
                </div>
                <ul className="mt-2 space-y-0.5 border-t border-slate-100 pt-1.5">
                  {g.lines.map((li, i) => (
                    <li key={i} className="flex gap-2 text-[11px] text-slate-600">
                      <span className="font-mono text-slate-400 shrink-0">{li.sku || "—"}</span>
                      <span className="flex-1 truncate">{li.name}</span>
                      <span className="shrink-0 text-slate-500">{li.quantity} {li.uom}</span>
                    </li>
                  ))}
                  {!g.lines.length && (
                    <li className="text-[11px] text-amber-700">No lines tagged to this PO yet</li>
                  )}
                </ul>
                <button type="button"
                  onClick={() => removePoFromOrder(g.po_number)}
                  className="mt-2 text-[10px] font-bold text-red-600 hover:text-red-700"
                  data-testid={`order-po-remove-${g.po_number}`}>
                  Remove this PO and its lines
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          onClick={() => save({ autoCompose: true })}
          disabled={saving}
          className="bg-cyan-600 hover:bg-cyan-700 text-white"
          data-testid="order-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Order &amp; Compose Email
        </Button>
        <Button
          type="button"
          onClick={() => save()}
          disabled={saving}
          variant="outline"
          className="border-slate-300 text-slate-700"
          data-testid="order-save-only-btn"
        >
          <Save className="w-4 h-4 mr-2" /> Save only
        </Button>
        <Button
          type="button"
          onClick={preview}
          variant="outline"
          data-testid="order-preview-btn"
        >
          <Eye className="w-4 h-4 mr-2" />
          Save + Preview PDF
        </Button>
        <span className="ml-2 text-[11px] text-slate-500 inline-flex items-center gap-1.5">
          <ClipboardList className="w-3.5 h-3.5" />
          The PDF is the packing / fulfillment form (SKU, description, qty, UOM, signature block).
        </span>
      </div>

      {/* ── Customer PO intake — AskIsaac reads the PO and writes the order ── */}
      <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-4" data-testid="po-intake-panel">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-violet-800 inline-flex items-center gap-1.5">
              <FileScan className="w-3.5 h-3.5" />
              Read a Customer Purchase Order
            </div>
            <p className="text-[11px] text-violet-900/70 mt-0.5 max-w-2xl">
              Drop their PO and AskIsaac fills the order — PO number, dates, ship-to, bill-to, buyer,
              terms, freight and every line with qty, UOM and unit price. Nothing is overwritten until
              you apply it. The PO price wins, and any gap against your quote is flagged.
            </p>
          </div>
          <label className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-semibold text-white cursor-pointer ${poReading ? "bg-slate-400" : "bg-violet-600 hover:bg-violet-700"}`}
            data-testid="po-upload-btn">
            {poReading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
            {poReading ? "AskIsaac is reading…" : "Upload PO"}
            <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp"
              disabled={poReading}
              onChange={(e) => { readPurchaseOrder(e.target.files?.[0]); e.target.value = ""; }}
              data-testid="po-file-input" />
          </label>
        </div>

        {poResult?.proposal && (
          <div className="mt-3 rounded-md border border-violet-200 bg-white p-3" data-testid="po-review-panel">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
              <p className="text-xs font-black uppercase tracking-wider text-violet-800">
                What AskIsaac read
                {poResult.applied ? <span className="ml-2 text-emerald-600">· applied</span> : null}
              </p>
              <div className="flex items-center gap-1.5">
                <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]"
                  onClick={() => applyPoProposal({ withLines: false })}
                  data-testid="po-apply-header-btn">
                  Apply header only
                </Button>
                {hasExistingPo && (
                  <Button type="button" size="sm" variant="outline"
                    className="h-7 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    onClick={addPoToOrder}
                    title="Keep the existing PO and add this one's lines to the same order (same ship-to only)"
                    data-testid="po-add-additional-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add as additional PO
                  </Button>
                )}
                <Button type="button" size="sm" className="h-7 text-[11px] bg-violet-600 hover:bg-violet-700 text-white"
                  onClick={() => applyPoProposal({ withLines: true })}
                  data-testid="po-apply-all-btn">
                  {hasExistingPo ? "Replace order" : "Apply to order"}
                </Button>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-x-4 gap-y-1.5 text-[11px]">
              {[
                ["PO number", poResult.proposal.po_number],
                ["PO date", poResult.proposal.po_date],
                ["Need by", poResult.proposal.po_need_by],
                ["Customer", poResult.proposal.customer_name],
                ["Buyer", [poResult.proposal.buyer_name, poResult.proposal.buyer_phone].filter(Boolean).join(" · ")],
                ["Terms", poResult.proposal.terms],
                ["Freight terms", poResult.proposal.freight_terms],
                ["Ship via", [poResult.proposal.ship_via, poResult.proposal.shipping_account].filter(Boolean).join(" · ")],
                ["Tax exempt", poResult.proposal.tax_exempt_id],
                ["PO total", poResult.proposal.po_total ? `$${Number(poResult.proposal.po_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : ""],
              ].filter(([, v]) => v).map(([k, v]) => (
                <div key={k}>
                  <span className="block text-[9px] font-black uppercase tracking-wider text-slate-400">{k}</span>
                  <span className="text-slate-800">{v}</span>
                </div>
              ))}
            </div>

            {(poResult.proposal.ship_to || poResult.proposal.bill_to) && (
              <div className="grid sm:grid-cols-2 gap-3 mt-2.5">
                {["ship_to", "bill_to"].map((k) => poResult.proposal[k] ? (
                  <div key={k} className="rounded bg-slate-50 border border-slate-200 p-2">
                    <span className="block text-[9px] font-black uppercase tracking-wider text-slate-400">
                      {k === "ship_to" ? "Ship to" : "Bill to"}
                    </span>
                    <span className="text-[11px] text-slate-700 whitespace-pre-line">{poResult.proposal[k]}</span>
                  </div>
                ) : null)}
              </div>
            )}

            {(poResult.proposal.line_items || []).length > 0 && (
              <div className="mt-2.5 border border-slate-200 rounded overflow-hidden">
                <div className="bg-slate-100 text-[9px] font-black uppercase tracking-wider text-slate-500 grid grid-cols-[90px_1fr_50px_50px_80px_85px] gap-2 px-2 py-1">
                  <div>Item</div><div>Description</div><div className="text-center">Qty</div>
                  <div>UOM</div><div className="text-right">Unit $</div><div className="text-right">Ext $</div>
                </div>
                {poResult.proposal.line_items.map((li, i) => (
                  <div key={i} className="grid grid-cols-[90px_1fr_50px_50px_80px_85px] gap-2 px-2 py-1.5 border-t border-slate-100 text-[11px]"
                    data-testid={`po-line-${i}`}>
                    <div className="font-mono text-slate-700 truncate">{li.sku || "—"}</div>
                    <div className="text-slate-800">
                      {li.name}
                      {li.notes ? <span className="block text-[10px] text-slate-400">{li.notes}</span> : null}
                    </div>
                    <div className="text-center text-slate-700">{li.quantity}</div>
                    <div className="text-slate-700">{li.uom}</div>
                    <div className="text-right text-slate-700">${Number(li.unit_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    <div className="text-right font-semibold text-slate-900">
                      ${Number(li.po_extended_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(poResult.duplicates || []).length > 0 && (
              <div className="mt-2.5 rounded border border-red-300 bg-red-50 p-2.5" data-testid="po-upload-duplicate-warning">
                <p className="text-[11px] font-black uppercase tracking-wider text-red-800 inline-flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> This PO number is already on file
                </p>
                <ul className="mt-1 space-y-0.5">
                  {poResult.duplicates.map((d) => (
                    <li key={d.backorder_id} className="text-[11px] text-red-900">
                      <button type="button" onClick={() => onOpenOrder?.(d.backorder_id)}
                        className="font-bold underline hover:no-underline"
                        data-testid={`po-upload-dupe-${d.backorder_id}`}>
                        {d.backorder_id}
                      </button>
                      {d.hospital_name ? ` · ${d.hospital_name}` : ""}
                      {d.po_number ? ` · PO ${d.po_number}` : ""}
                      {d.status ? ` · ${String(d.status).replace(/_/g, " ")}` : ""}
                      {d.match === "partial" ? " · near match" : ""}
                    </li>
                  ))}
                </ul>
                <p className="text-[10px] text-red-800/80 mt-1">
                  Applying it here puts the same PO on two orders. Fine for a revision — otherwise open
                  the order above instead.
                </p>
              </div>
            )}

            {(poResult.quote_variance || []).length > 0 && (
              <div className="mt-2.5 rounded border border-amber-300 bg-amber-50 p-2.5" data-testid="po-variance-warning">
                <p className="text-[11px] font-black uppercase tracking-wider text-amber-800 inline-flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> Their PO price differs from your quote
                </p>
                <ul className="mt-1 space-y-0.5">
                  {poResult.quote_variance.map((v, i) => (
                    <li key={i} className="text-[11px] text-amber-900">
                      {v.sku || v.name}: quoted ${v.quoted_unit_price.toFixed(2)} → PO ${v.po_unit_price.toFixed(2)}
                      <span className={v.delta < 0 ? "font-bold text-red-700" : "font-bold text-emerald-700"}>
                        {" "}({v.delta > 0 ? "+" : ""}{v.delta.toFixed(2)} / {v.delta_pct > 0 ? "+" : ""}{v.delta_pct}%)
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-[10px] text-amber-800/80 mt-1">
                  Applying the PO takes their price. Sort out the difference before you ship if it was not agreed.
                </p>
              </div>
            )}

            {(poResult.proposal.confidence_notes || []).length > 0 && (
              <p className="mt-2 text-[10px] text-slate-500">
                <strong>Check these:</strong> {poResult.proposal.confidence_notes.join(" · ")}
              </p>
            )}

            {poResult.attachment ? (
              <a href={`${API}${poResult.attachment.url}`} target="_blank" rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 hover:underline">
                <Eye className="w-3 h-3" /> Open the PO we read ({poResult.attachment.name})
              </a>
            ) : null}
          </div>
        )}
      </div>

      {/* Sales order confirmation form — the admin's own paperwork
          (QuickBooks PDF, signed sales order) that rides along with the
          confirmation email. */}
      <div className="rounded-lg border border-cyan-200 bg-cyan-50/50 p-4" data-testid="order-form-uploads">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-cyan-800 inline-flex items-center gap-1.5">
              <Paperclip className="w-3.5 h-3.5" />
              Sales Order Confirmation Form
            </div>
            <p className="text-[11px] text-cyan-900/70 mt-0.5">
              Upload your signed sales order or QuickBooks confirmation. It gets attached to the
              confirmation email automatically, or downloaded ready to drag into Outlook.
            </p>
          </div>
          <label className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-semibold text-white cursor-pointer ${uploading ? "bg-slate-400" : "bg-cyan-600 hover:bg-cyan-700"}`}
            data-testid="order-form-upload-btn">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {uploading ? "Uploading…" : "Upload form"}
            <input
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              disabled={uploading}
              onChange={(e) => { uploadForm(e.target.files?.[0]); e.target.value = ""; }}
              data-testid="order-form-file-input"
            />
          </label>
        </div>
        {attachments.length > 0 ? (
          <div className="mt-3 divide-y divide-cyan-100 border border-cyan-200 rounded-md bg-white">
            {attachments.map((att) => (
              <div key={att.id} className="flex items-center justify-between gap-3 px-3 py-2"
                data-testid={`order-form-row-${att.id}`}>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-800 truncate">{att.name}</p>
                  <p className="text-[10px] text-slate-500">
                    {(att.size / 1024).toFixed(0)} KB · uploaded {(att.uploaded_at || "").slice(0, 10)}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <a href={fileHref(att)} target="_blank" rel="noreferrer"
                    className="p-1.5 text-slate-500 hover:text-slate-800" title="Open">
                    <Eye className="w-4 h-4" />
                  </a>
                  <a href={fileHref(att)} download={att.name}
                    className="p-1.5 text-slate-500 hover:text-slate-800" title="Download">
                    <Download className="w-4 h-4" />
                  </a>
                  <button type="button" onClick={() => removeForm(att)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Remove"
                    data-testid={`order-form-remove-${att.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-[11px] text-slate-500">
            No form uploaded yet — PDF, Word, Excel or an image all work (15 MB max).
          </p>
        )}
      </div>

      {/* Hospital notifications — mirrors the Fulfillment tab's milestone
          buttons but drives them from this order's tracking + line items so
          admins can ship-notify without switching tabs. */}
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-800 inline-flex items-center gap-1.5">
              <Truck className="w-3.5 h-3.5" />
              Send Fulfillment Update to {backorder.hospital_name || "Hospital"}
            </div>
            <p className="text-[11px] text-emerald-900/70 mt-0.5">
              Uses this order&rsquo;s tracking #, courier, and line items. Emails come from the same
              branded template as the Fulfillment tab.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            onClick={openConfirmDialog}
            disabled={notifying !== null}
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="order-notify-confirmed-btn"
            title="Order confirmed — compose email with editable recipients"
          >
            {notifying === "confirmed"
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <FileCheck2 className="w-4 h-4 mr-2" />}
            Send Order Confirmation
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={sendShipped}
            disabled={notifying !== null}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="order-notify-shipped-btn"
            title={draft.tracking_number ? "" : "Add a tracking # first"}
          >
            {notifying === "shipped"
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Truck className="w-4 h-4 mr-2" />}
            Send Tracking (Shipped)
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={sendArrivingToday}
            disabled={notifying !== null}
            className="border-sky-300 text-sky-800 hover:bg-sky-50"
            data-testid="order-notify-arriving-today-btn"
            title="Tell receiving the shipment lands today"
          >
            {notifying === "arriving_today"
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Clock className="w-4 h-4 mr-2" />}
            Send Arriving Today
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={sendDelay}
            disabled={notifying !== null}
            className="border-amber-300 text-amber-800 hover:bg-amber-50"
            data-testid="order-notify-delay-btn"
          >
            {notifying === "delayed"
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <AlertTriangle className="w-4 h-4 mr-2" />}
            Send Delay Notice
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={sendDelivered}
            disabled={notifying !== null}
            className="border-emerald-400 text-emerald-800 hover:bg-emerald-100"
            data-testid="order-notify-delivered-btn"
          >
            {notifying === "arrived"
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Send Delivery Confirmation
          </Button>
        </div>
      </div>

      {/* ─────────── Order-Confirmation Compose Dialog ───────────
          Editable To/CC recipients. Sends via Sterile Mate (Graph) OR
          opens a mailto: link in the admin's default mail client
          (Outlook / Apple Mail) for manual send. */}
      {confirmDialog.open && (
        <div className="fixed inset-0 z-[80] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="order-confirm-dialog">
          <div className="bg-white w-full max-w-xl rounded-xl shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3 bg-slate-50 rounded-t-xl">
              <div>
                <p className="text-[10px] font-black uppercase tracking-wider text-cyan-700">Send Order Confirmation</p>
                <h3 className="text-lg font-extrabold text-slate-900">Compose &amp; Send</h3>
              </div>
              <Button variant="ghost" size="icon" onClick={closeConfirmDialog} data-testid="order-confirm-close">
                <span aria-hidden>×</span>
              </Button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-600">To (comma-separated)</label>
                  <Button type="button" size="sm" variant="ghost"
                    className="h-6 px-2 text-[10px] font-bold text-cyan-800 hover:bg-cyan-50"
                    onClick={() => setCrmPicker("to")}
                    data-testid="order-confirm-crm-to-btn">
                    <Users className="w-3 h-3 mr-1" /> Pick from CRM
                  </Button>
                </div>
                <Input
                  value={confirmDialog.to}
                  onChange={(e) => setConfirmDialog(d => ({ ...d, to: e.target.value }))}
                  placeholder="buyer@hospital.org"
                  data-testid="order-confirm-to"
                />
              </div>
              <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-600">Cc (optional, comma-separated)</label>
                  <Button type="button" size="sm" variant="ghost"
                    className="h-6 px-2 text-[10px] font-bold text-slate-600 hover:bg-slate-100"
                    onClick={() => setCrmPicker("cc")}
                    data-testid="order-confirm-crm-cc-btn">
                    <Users className="w-3 h-3 mr-1" /> Pick from CRM
                  </Button>
                </div>
                <Input
                  value={confirmDialog.cc}
                  onChange={(e) => setConfirmDialog(d => ({ ...d, cc: e.target.value }))}
                  placeholder="materials-mgr@hospital.org, ap@hospital.org"
                  data-testid="order-confirm-cc"
                />
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                <p className="text-[11px] text-slate-600">
                  {recipientsSavedAt
                    ? "Saved to this order — they will be here next time, and the arriving-today notice uses them."
                    : "Save these so they stick to the order and feed the shipped / arriving-today notices."}
                </p>
                <Button type="button" size="sm" variant="outline"
                  onClick={() => saveRecipients()} disabled={savingRecipients}
                  className="h-7 text-[11px] border-cyan-300 text-cyan-800 hover:bg-cyan-50"
                  data-testid="order-confirm-save-recipients">
                  {savingRecipients
                    ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    : <Save className="w-3 h-3 mr-1" />}
                  Save recipients
                </Button>
              </div>
              <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-600">
                    Message — built from the order (edit freely)
                  </label>
                  <Button type="button" size="sm" variant="ghost"
                    className="h-6 px-2 text-[10px] font-bold text-slate-600 hover:bg-slate-100"
                    onClick={() => setConfirmDialog(d => ({ ...d, body: confirmBodyPlain() }))}
                    data-testid="order-confirm-rebuild-body">
                    <Wand2 className="w-3 h-3 mr-1" /> Rebuild from order
                  </Button>
                </div>
                {/* Saved wording — tokens re-fill from whatever order you are on */}
                <div className="flex items-center gap-1.5 mb-1.5">
                  <select
                    value={activeTemplateId}
                    onChange={(e) => applyLetterTemplate(e.target.value)}
                    className="flex-1 h-7 rounded border border-slate-300 bg-white text-[11px] px-2 text-slate-700"
                    data-testid="order-letter-template-select"
                  >
                    <option value="">Built-in wording</option>
                    {letterTemplates.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name}{t.is_default ? " (default)" : ""}
                      </option>
                    ))}
                  </select>
                  <Button type="button" size="sm" variant="outline"
                    className="h-7 px-2 text-[10px] font-bold border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                    onClick={saveLetterTemplate}
                    data-testid="order-letter-template-save">
                    <Save className="w-3 h-3 mr-1" /> Save as template
                  </Button>
                  {activeTemplateId && (
                    <Button type="button" size="sm" variant="ghost"
                      className="h-7 px-2 text-[10px] font-bold text-rose-600 hover:bg-rose-50"
                      onClick={deleteLetterTemplate}
                      data-testid="order-letter-template-delete">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
                <Textarea
                  value={confirmDialog.body}
                  onChange={(e) => setConfirmDialog(d => ({ ...d, body: e.target.value }))}
                  rows={12}
                  className="text-xs font-mono"
                  data-testid="order-confirm-body"
                />
              </div>

              {/* Which forms ride along */}
              <div className="rounded-lg border border-cyan-200 bg-cyan-50/60 p-3" data-testid="order-confirm-attachments">
                <p className="text-[10px] font-black uppercase tracking-wider text-cyan-800 mb-1.5 inline-flex items-center gap-1.5">
                  <Paperclip className="w-3.5 h-3.5" /> Attach sales order form
                </p>
                <label className="flex items-center gap-2 text-xs text-slate-800 cursor-pointer mb-2 font-semibold">
                  <input
                    type="checkbox"
                    checked={attachQuotePdf}
                    onChange={(e) => setAttachQuotePdf(e.target.checked)}
                    data-testid="order-confirm-attach-quote"
                  />
                  Attach the quote PDF{quoteNumber ? ` (${quoteNumber})` : ""} — the letter says it is attached
                </label>
                {attachments.length === 0 ? (
                  <p className="text-[11px] text-slate-600">
                    Nothing uploaded yet — close this and use <strong>Upload form</strong> on the order to
                    include your signed sales order or QuickBooks confirmation.
                  </p>
                ) : (
                  <div className="space-y-1">
                    {attachments.map((att) => (
                      <label key={att.id} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={attachIds.includes(att.id)}
                          onChange={(e) => setAttachIds(ids =>
                            e.target.checked ? [...ids, att.id] : ids.filter(i => i !== att.id))}
                          data-testid={`order-confirm-attach-${att.id}`}
                        />
                        <span className="truncate">{att.name}</span>
                        <span className="text-[10px] text-slate-400">{(att.size / 1024).toFixed(0)} KB</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-[11px] text-slate-500">
                <strong className="text-slate-700">Send via Sterile Mate</strong> emails it through your branded Graph mailer with the ticked forms
                attached — nothing to drag, and it logs a confirmed milestone on the order.
                <strong className="text-slate-700"> Open in Outlook (attached)</strong> writes the message into your
                orders@sterilemate.com Drafts folder with the ticked forms <em>already attached</em> and opens it —
                press Send yourself. <strong className="text-slate-700">mailto draft</strong> is the old fallback: it
                opens your desktop mail client and downloads the forms to drag in, because a mailto link cannot
                carry a file.
              </p>
            </div>
            <div className="border-t border-slate-200 px-5 py-3 bg-slate-50 rounded-b-xl flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={closeConfirmDialog} data-testid="order-confirm-cancel">Cancel</Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={sendConfirmed}
                  disabled={notifying !== null}
                  data-testid="order-confirm-send-btn"
                >
                  {notifying === "confirmed"
                    ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    : <FileCheck2 className="w-4 h-4 mr-1.5" />}
                  Send via Sterile Mate
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={openInOutlook}
                  className="text-slate-600 hover:bg-slate-100"
                  title="mailto: draft — downloads the ticked forms for you to drag in"
                  data-testid="order-confirm-outlook-btn"
                >
                  <Mail className="w-4 h-4 mr-1.5" /> mailto draft
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={draftInOutlook}
                  disabled={draftingOutlook || notifying !== null}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white"
                  data-testid="order-confirm-outlook-draft-btn"
                >
                  {draftingOutlook
                    ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    : <ExternalLink className="w-4 h-4 mr-1.5" />}
                  Open in Outlook (attached)
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CRM address book — pick the acknowledgment recipients from the CRM
          instead of typing an address from memory. */}
      <CrmContactPicker
        open={!!crmPicker}
        onOpenChange={(o) => !o && setCrmPicker(null)}
        mode="multi"
        title={crmPicker === "cc" ? "Add Cc recipients from CRM" : "Add To recipients from CRM"}
        confirmLabel={crmPicker === "cc" ? "Add to Cc" : "Add to To"}
        onConfirm={(contacts) => { addRecipients(contacts, crmPicker || "to"); setCrmPicker(null); }}
        hospitalId={backorder.hospital_id || ""}
        hospitalName={backorder.hospital_name || ""}
      />
    </div>
  );
}
